import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Bell, Zap, Shield, BarChart3, ArrowRight, Check, Layout as LayoutIcon, MousePointer2, Layers, Globe, CreditCard, Mail, MessageSquare, Lock } from 'lucide-react';
interface AgentToBossProps {
  onBack?: () => void;
}
export function AgentToBoss({
  onBack
}: AgentToBossProps) {
  const [activeFeature, setActiveFeature] = useState('contacts');
  const [view, setView] = useState<'home' | 'domains' | 'platform' | 'demo' | 'enterprise' | 'pricing' | 'client-dashboard'>('home');
  const [activeDashboardTab, setActiveDashboardTab] = useState('dashboard');
  const features = [{
    id: 'contacts',
    title: 'Contact Management',
    desc: 'Centralize every interaction. Our intelligent CRM automatically enriches lead data and tracks communication history across all channels.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'leads',
    title: 'Lead Tracking',
    desc: 'Visualize your funnel with precision. Move leads through custom stages and let AI predict conversion probability based on engagement.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'reminders',
    title: 'Instant Reminders',
    desc: 'Never miss a follow-up. Agentic triggers notify you exactly when a lead is most likely to engage, ensuring maximum ROI on every touchpoint.',
    image: 'https://images.unsplash.com/photo-1551288049-bbbda5366392?auto=format&fit=crop&q=80&w=1200'
  }];
  return <div className="min-h-screen bg-[#F3F6F9] text-[#1B264F] font-sans selection:bg-[#00A1E0] selection:text-white" data-id="element-227">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#D8E2EF] px-8 py-4" data-id="element-228">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-229">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setView('home')} data-id="element-230">
            <div className="w-10 h-10 bg-[#00A1E0] rounded-lg flex items-center justify-center text-white shadow-lg shadow-[#00A1E0]/20" data-id="element-231">
              <Zap size={24} fill="currentColor" data-id="element-232" />
            </div>
            <span className="text-xl font-black tracking-tight text-[#1B264F]" data-id="element-233">
              Agent<span className="text-[#00A1E0]" data-id="element-234">to</span>boss
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-bold text-[#1B264F]" data-id="element-235">
            <button onClick={() => setView('home')} className={`hover:text-[#00A1E0] transition-colors ${view === 'home' ? 'text-[#00A1E0]' : ''}`} data-id="element-236">
              Home
            </button>
            <button onClick={() => setView('platform')} className={`hover:text-[#00A1E0] transition-colors ${view === 'platform' ? 'text-[#00A1E0]' : ''}`} data-id="element-237">
              Platform
            </button>
            <button onClick={() => setView('domains')} className={`hover:text-[#00A1E0] transition-colors ${view === 'domains' ? 'text-[#00A1E0]' : ''}`} data-id="element-238">
              Domains & Hosting
            </button>
            <button onClick={() => setView('enterprise')} className={`hover:text-[#00A1E0] transition-colors ${view === 'enterprise' ? 'text-[#00A1E0]' : ''}`} data-id="element-239">
              Enterprise
            </button>
            <button onClick={() => setView('pricing')} className={`hover:text-[#00A1E0] transition-colors ${view === 'pricing' ? 'text-[#00A1E0]' : ''}`} data-id="element-240">
              Pricing
            </button>
          </div>

          <div className="flex items-center gap-4" data-id="element-241">
            {onBack && <button onClick={onBack} className="text-xs font-bold uppercase tracking-widest text-[#1B264F]/60 hover:text-[#1B264F] transition-colors mr-4" data-id="element-242">
                ← Back to Admin
              </button>}
            <button onClick={() => setView('client-dashboard')} className="px-6 py-2.5 border-2 border-[#00A1E0] text-[#00A1E0] rounded-full text-sm font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-243">
              Client Login
            </button>
            <button onClick={() => setView('demo')} className="px-6 py-2.5 bg-[#00A1E0] text-white rounded-full text-sm font-bold hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-244">
              Demo
            </button>
          </div>
        </div>
      </nav>

      {view === 'client-dashboard' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="flex min-h-[calc(100vh-73px)]" data-id="element-245">
          {/* Dashboard Sidebar */}
          <div className="w-64 bg-[#1B264F] text-white p-6 space-y-2" data-id="element-246">
            {[{
          id: 'dashboard',
          label: 'Dashboard',
          icon: LayoutIcon
        }, {
          id: 'domains',
          label: 'Domains',
          icon: Globe
        }, {
          id: 'dns',
          label: 'DNS Management',
          icon: Layers
        }, {
          id: 'billing',
          label: 'Billing',
          icon: CreditCard
        }, {
          id: 'email-marketing',
          label: 'E-mail Marketing',
          icon: Mail
        }, {
          id: 'crm',
          label: 'CRM',
          icon: Users
        }, {
          id: 'sms',
          label: 'SMS Blast',
          icon: MessageSquare
        }, {
          id: 'pro-email',
          label: 'Pro Email',
          icon: Shield
        }, {
          id: 'ssl',
          label: 'SSL Certificates',
          icon: Lock
        }].map(tab => <button key={tab.id} onClick={() => setActiveDashboardTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeDashboardTab === tab.id ? 'bg-[#00A1E0] text-white shadow-lg shadow-[#00A1E0]/20' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-id="element-247">
                <tab.icon size={18} data-id="element-248" />
                {tab.label}
              </button>)}
          </div>

          {/* Dashboard Content */}
          <div className="flex-1 p-10 overflow-y-auto" data-id="element-249">
            <div className="max-w-5xl mx-auto" data-id="element-250">
              <div className="flex justify-between items-end mb-10" data-id="element-251">
                <div data-id="element-252">
                  <h2 className="text-3xl font-black text-[#1B264F] uppercase tracking-tight" data-id="element-253">
                    {activeDashboardTab.replace('-', ' ')}
                  </h2>
                  <p className="text-slate-500 font-medium" data-id="element-254">
                    Manage your enterprise assets and communications.
                  </p>
                </div>
                {activeDashboardTab === 'sms' && <button className="px-6 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm shadow-lg shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-255">
                    Add SMS Credit
                  </button>}
              </div>

              <div className="bg-white rounded-[32px] border border-[#D8E2EF] shadow-sm p-8 min-h-[500px]" data-id="element-256">
                {activeDashboardTab === 'dashboard' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-257">
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-258">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-259">
                        Active Domains
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-260">4</p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-261">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-262">
                        CRM Leads
                      </p>
                      <p className="text-2xl font-black text-[#00A1E0]" data-id="element-263">
                        1,284
                      </p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-264">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-265">
                        SMS Balance
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-266">
                        $42.50
                      </p>
                    </div>
                  </div>}

                {activeDashboardTab === 'domains' && <div className="space-y-4" data-id="element-267">
                    {['agenttoboss.com', 'myagency.io', 'realtypro.net'].map(domain => <div key={domain} className="flex items-center justify-between p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-268">
                          <div className="flex items-center gap-3" data-id="element-269">
                            <Globe size={20} className="text-[#00A1E0]" data-id="element-270" />
                            <span className="font-bold text-[#1B264F]" data-id="element-271">
                              {domain}
                            </span>
                          </div>
                          <div className="flex gap-2" data-id="element-272">
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-273">
                              Manage
                            </button>
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-274">
                              DNS
                            </button>
                          </div>
                        </div>)}
                  </div>}

                {activeDashboardTab === 'sms' && <div className="space-y-8" data-id="element-275">
                    <div className="p-8 bg-[#1B264F] text-white rounded-2xl relative overflow-hidden" data-id="element-276">
                      <div className="relative z-10" data-id="element-277">
                        <p className="text-xs font-black uppercase text-white/40 mb-2" data-id="element-278">
                          Available Credit
                        </p>
                        <p className="text-5xl font-black" data-id="element-279">$42.50</p>
                        <p className="text-sm text-white/60 mt-4" data-id="element-280">
                          Estimated 2,360 SMS segments remaining.
                        </p>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-281"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-282">
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-283">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-284">
                          Quick Blast
                        </h4>
                        <textarea className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl p-4 text-sm focus:outline-none min-h-[100px]" placeholder="Enter your SMS message..." data-id="element-285" />
                        <button className="w-full mt-4 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm" data-id="element-286">
                          Send Blast
                        </button>
                      </div>
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-287">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-288">
                          Auto-Topup
                        </h4>
                        <div className="flex items-center justify-between p-3 bg-[#F3F6F9] rounded-xl" data-id="element-289">
                          <span className="text-sm font-bold" data-id="element-290">
                            Enable Auto-Topup
                          </span>
                          <div className="w-10 h-5 bg-[#00A1E0] rounded-full relative" data-id="element-291">
                            <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-292"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Placeholder for other tabs */}
                {['dns', 'billing', 'email-marketing', 'crm', 'pro-email', 'ssl'].includes(activeDashboardTab) && activeDashboardTab !== 'sms' && activeDashboardTab !== 'domains' && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-293">
                      <div className="w-20 h-20 bg-[#F3F6F9] rounded-3xl flex items-center justify-center text-[#00A1E0] mb-6" data-id="element-294">
                        <Zap size={40} data-id="element-295" />
                      </div>
                      <h3 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-296">
                        Enterprise {activeDashboardTab.replace('-', ' ')}
                      </h3>
                      <p className="text-slate-500 max-w-xs mx-auto" data-id="element-297">
                        Your {activeDashboardTab} services are active and being
                        managed by our enterprise architects.
                      </p>
                    </div>}
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'pricing' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="py-20 px-8" data-id="element-298">
          <div className="max-w-7xl mx-auto" data-id="element-299">
            <div className="text-center mb-20" data-id="element-300">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-301">
                Transparent Comparison
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-302">
                See how Agenttoboss stacks up against traditional CRM and
                marketing platforms.
              </p>
            </div>

            <div className="overflow-x-auto" data-id="element-303">
              <table className="w-full border-collapse bg-white rounded-[32px] overflow-hidden shadow-2xl border border-[#D8E2EF]" data-id="element-304">
                <thead data-id="element-305">
                  <tr className="bg-[#1B264F] text-white" data-id="element-306">
                    <th className="p-8 text-left text-lg font-black" data-id="element-307">
                      Feature
                    </th>
                    <th className="p-8 text-center text-lg font-black bg-[#00A1E0]" data-id="element-308">
                      Agenttoboss
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-309">
                      Traditional CRMs
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-310">
                      Marketing Agencies
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#1B264F]" data-id="element-311">
                  {[{
                f: 'Custom 5-Pager Website',
                a: 'Included (Free)',
                b: '$2,500+ Setup',
                c: '$5,000+ Setup'
              }, {
                f: 'CRM + 100k Email Blasts',
                a: 'Included (Free)',
                b: '$150+/mo',
                c: 'N/A'
              }, {
                f: 'Social Media (4 Channels)',
                a: '$150/mo',
                b: 'N/A',
                c: '$1,000+/mo'
              }, {
                f: 'AI Telephony (Tier 2)',
                a: '$119/seat',
                b: '$250+/seat',
                c: 'N/A'
              }, {
                f: 'Review Management',
                a: 'Included',
                b: 'Add-on ($50/mo)',
                c: 'Included'
              }, {
                f: 'Business Consulting',
                a: 'Included (Advance)',
                b: 'N/A',
                c: '$200+/hr'
              }].map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F3F6F9]'} data-id="element-312">
                      <td className="p-8 font-bold border-b border-[#D8E2EF]" data-id="element-313">
                        {row.f}
                      </td>
                      <td className="p-8 text-center font-black text-[#00A1E0] border-b border-[#D8E2EF] bg-[#00A1E0]/5" data-id="element-314">
                        {row.a}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-315">
                        {row.b}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-316">
                        {row.c}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-20 bg-[#1B264F] rounded-[40px] p-12 text-white text-center" data-id="element-317">
              <h2 className="text-3xl font-black mb-6" data-id="element-318">
                Ready to switch and save?
              </h2>
              <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all" data-id="element-319">
                Get a Custom Quote
              </button>
            </div>
          </div>
        </motion.div>}

      {view === 'enterprise' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="overflow-hidden" data-id="element-320">
          {/* Enterprise Hero */}
          <section className="relative py-32 px-8 bg-[#1B264F] text-white" data-id="element-321">
            <div className="absolute inset-0 opacity-20" data-id="element-322">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000" alt="Enterprise Background" className="w-full h-full object-cover" data-id="element-323" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/80 to-transparent" data-id="element-324"></div>
            </div>

            <div className="w-full mx-auto relative z-10 px-8" data-id="element-325">
              <div className="max-w-3xl" data-id="element-326">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-black uppercase tracking-widest mb-8" data-id="element-327">
                  Enterprise Solutions
                </div>
                <h1 className="text-6xl lg:text-8xl font-black mb-8 tracking-tighter leading-[0.9]" data-id="element-328">
                  Built for the <br data-id="element-329" />
                  <span className="text-[#00A1E0]" data-id="element-330">1% of Agencies.</span>
                </h1>
                <p className="text-xl text-white/60 font-medium leading-relaxed mb-10" data-id="element-331">
                  Scale without limits. Our enterprise tier provides the
                  infrastructure, security, and custom engineering required by
                  the world's largest real estate and sales organizations.
                </p>
                <div className="flex gap-4" data-id="element-332">
                  <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30" data-id="element-333">
                    Speak with an Architect
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Custom Integrations Section */}
          <section className="py-32 px-8 bg-white" data-id="element-334">
            <div className="max-w-7xl mx-auto" data-id="element-335">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-336">
                <div data-id="element-337">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-8 tracking-tight" data-id="element-338">
                    Custom Engineering & <br data-id="element-339" />
                    <span className="text-[#00A1E0]" data-id="element-340">Deep Integrations</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-12" data-id="element-341">
                    Off-the-shelf software doesn't cut it at your scale. Our
                    dedicated engineering team builds custom bridges between
                    Agenttoboss and your existing tech stack.
                  </p>

                  <div className="space-y-8" data-id="element-342">
                    <div className="flex gap-6" data-id="element-343">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-344">
                        <Zap size={28} data-id="element-345" />
                      </div>
                      <div data-id="element-346">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-347">
                          Enterprise Telephony
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-348">
                          Custom SIP trunking, advanced IVR flows, and global
                          carrier integrations tailored to your specific call
                          center requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-349">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-350">
                        <Layers size={28} data-id="element-351" />
                      </div>
                      <div data-id="element-352">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-353">
                          Proprietary AI Workflows
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-354">
                          We deploy custom LLM agents trained on your unique
                          business data to automate lead qualification and
                          complex back-office tasks.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-355">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-356">
                        <Shield size={28} data-id="element-357" />
                      </div>
                      <div data-id="element-358">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-359">
                          SSO & Governance
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-360">
                          Enterprise-grade security with SAML/SSO, custom RBAC,
                          and comprehensive audit logging for compliance-heavy
                          environments.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-361">
                  <div className="bg-[#1B264F] rounded-[40px] p-12 shadow-2xl relative z-10" data-id="element-362">
                    <div className="space-y-6" data-id="element-363">
                      <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl" data-id="element-364">
                        <span className="text-white/60 font-bold" data-id="element-365">
                          API Status
                        </span>
                        <span className="text-[#00A1E0] font-black" data-id="element-366">
                          99.99% Uptime
                        </span>
                      </div>
                      <div className="h-48 bg-[#F3F6F9]/5 rounded-2xl border border-white/10 p-6" data-id="element-367">
                        <div className="flex gap-2 mb-4" data-id="element-368">
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]" data-id="element-369"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-370"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-371"></div>
                        </div>
                        <div className="space-y-2" data-id="element-372">
                          <div className="h-2 w-3/4 bg-white/10 rounded" data-id="element-373"></div>
                          <div className="h-2 w-1/2 bg-white/10 rounded" data-id="element-374"></div>
                          <div className="h-2 w-2/3 bg-white/10 rounded" data-id="element-375"></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-376">
                        <div className="p-4 bg-[#00A1E0] rounded-2xl text-center" data-id="element-377">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-378">
                            Endpoints
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-379">450+</p>
                        </div>
                        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl text-center" data-id="element-380">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-381">
                            Latency
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-382">12ms</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Decorative Glow */}
                  <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full" data-id="element-383"></div>
                </div>
              </div>
            </div>
          </section>

          {/* Enterprise CTA Form */}
          <section className="py-32 px-8 bg-[#F3F6F9]" data-id="element-384">
            <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl p-12" data-id="element-385">
              <div className="text-center mb-12" data-id="element-386">
                <h2 className="text-4xl font-black text-[#1B264F] mb-4" data-id="element-387">
                  Request Enterprise Access
                </h2>
                <p className="text-[#1B264F]/60 font-medium" data-id="element-388">
                  Our enterprise architects will reach out within 2 hours.
                </p>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-389">
                <div className="space-y-2" data-id="element-390">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-391">
                    Company Name
                  </label>
                  <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Global Realty Group" data-id="element-392" />
                </div>
                <div className="space-y-2" data-id="element-393">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-394">
                    Estimated Seats
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-395">
                    <option data-id="element-396">50-100 seats</option>
                    <option data-id="element-397">100-500 seats</option>
                    <option data-id="element-398">500+ seats</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-399">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-400">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="vp@globalrealty.com" data-id="element-401" />
                </div>
                <div className="space-y-2" data-id="element-402">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-403">
                    Request Type
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-404">
                    <option data-id="element-405">Book a Demo</option>
                    <option data-id="element-406">Request Callback</option>
                    <option data-id="element-407">Custom Integration Quote</option>
                  </select>
                </div>
                <button className="md:col-span-2 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-408">
                  Submit Enterprise Request
                </button>
              </form>
            </div>
          </section>
        </motion.div>}

      {view === 'platform' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="py-20 px-8" data-id="element-409">
          <div className="max-w-7xl mx-auto" data-id="element-410">
            <div className="mb-16" data-id="element-411">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-412">
                The Scaling Engine
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-3xl font-medium" data-id="element-413">
                Our platform is designed to eliminate the technical overhead of
                running a high-performance agency. Here is how we enable you to
                scale.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-414">
              <div className="space-y-8" data-id="element-415">
                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-416">
                  <div className="flex items-center gap-4 mb-6" data-id="element-417">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-418">
                      <LayoutIcon size={24} data-id="element-419" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-420">Websites & Hosting</h3>
                  </div>
                  <div className="space-y-6" data-id="element-421">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-422">
                      <div data-id="element-423">
                        <p className="font-bold text-[#1B264F]" data-id="element-424">
                          5-Pager Standard Website
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-425">
                          Free per our standards
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-426">
                        $20/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-427">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-428">
                      <div data-id="element-429">
                        <p className="font-bold text-[#1B264F]" data-id="element-430">
                          Enterprise (10+ Pages)
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-431">
                          Custom high-capacity architecture
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-432">
                        $65/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-433">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-434">
                      <div data-id="element-435">
                        <p className="font-bold text-[#1B264F]" data-id="element-436">
                          E-commerce Solution
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-437">
                          Full store integration
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-438">
                        $70/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-439">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-440">
                  <div className="flex items-center gap-4 mb-6" data-id="element-441">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-442">
                      <Target size={24} data-id="element-443" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-444">Marketing & CRM</h3>
                  </div>
                  <div className="space-y-6" data-id="element-445">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-446">
                      <div data-id="element-447">
                        <p className="font-bold text-[#1B264F]" data-id="element-448">
                          Social Media Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-449">
                          4 Accounts + GMB/Yelp Setup + Review Mgmt
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-450">$150/mo</p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-451">
                      <div data-id="element-452">
                        <p className="font-bold text-[#1B264F]" data-id="element-453">
                          CRM & Email Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-454">
                          First 100,000 email blasts per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-455">FREE</p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-456">
                      <div data-id="element-457">
                        <p className="font-bold text-[#1B264F]" data-id="element-458">
                          High Volume Email
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-459">
                          Up to 1M email sends per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-460">+$15/mo</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8" data-id="element-461">
                <div className="bg-[#1B264F] text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden" data-id="element-462">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-463"></div>
                  <div className="flex items-center gap-4 mb-8 relative z-10" data-id="element-464">
                    <div className="w-12 h-12 bg-[#00A1E0] rounded-2xl flex items-center justify-center text-white" data-id="element-465">
                      <Zap size={24} fill="currentColor" data-id="element-466" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-467">Phone Systems</h3>
                  </div>
                  <div className="space-y-8 relative z-10" data-id="element-468">
                    <div className="p-6 bg-white/5 border border-white/10 rounded-2xl" data-id="element-469">
                      <div className="flex justify-between items-center mb-4" data-id="element-470">
                        <h4 className="font-black text-[#00A1E0]" data-id="element-471">
                          Tier 1: Basic
                        </h4>
                        <p className="text-2xl font-black" data-id="element-472">
                          $35
                          <span className="text-xs text-white/40" data-id="element-473">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/60 mb-4" data-id="element-474">
                        Voice + CRM Integration. Perfect for small teams.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/40" data-id="element-475">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="p-6 bg-[#00A1E0] rounded-2xl shadow-xl" data-id="element-476">
                      <div className="flex justify-between items-center mb-4" data-id="element-477">
                        <h4 className="font-black text-white" data-id="element-478">
                          Tier 2: AI Advanced
                        </h4>
                        <p className="text-2xl font-black" data-id="element-479">
                          $119
                          <span className="text-xs text-white/70" data-id="element-480">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/90 mb-4" data-id="element-481">
                        Unlimited Voice + AI + Automation + Dedicated Support
                        Engineer.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70" data-id="element-482">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10" data-id="element-483">
                      <div className="flex justify-between text-xs font-bold" data-id="element-484">
                        <span className="text-white/60" data-id="element-485">SMS Segment</span>
                        <span data-id="element-486">$0.018</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold mt-2" data-id="element-487">
                        <span className="text-white/60" data-id="element-488">MMS Segment</span>
                        <span data-id="element-489">$0.050</span>
                      </div>
                      <p className="text-[10px] text-white/40 mt-4 italic text-center" data-id="element-490">
                        *Min $20 wallet required for SMS enablement
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-491">
                  <h3 className="text-xl font-black mb-6" data-id="element-492">
                    Add-ons & Essentials
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-493">
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-494">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-495">
                        SSL Certificate
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-496">
                        $30/year
                      </p>
                    </div>
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-497">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-498">
                        Professional Email
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-499">
                        5 FREE
                      </p>
                      <p className="text-[10px] text-[#1B264F]/40 font-bold" data-id="element-500">
                        $3/extra
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'demo' && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="py-20 px-8" data-id="element-501">
          <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl overflow-hidden flex flex-col md:flex-row" data-id="element-502">
            <div className="md:w-1/2 bg-[#1B264F] p-12 text-white flex flex-col justify-between" data-id="element-503">
              <div data-id="element-504">
                <h2 className="text-4xl font-black mb-6" data-id="element-505">
                  Experience the Power.
                </h2>
                <p className="text-white/60 font-medium leading-relaxed mb-8" data-id="element-506">
                  Schedule a 15-minute walkthrough of the Agenttoboss platform
                  and see how we can automate your growth.
                </p>
                <div className="space-y-6" data-id="element-507">
                  <div className="flex items-center gap-4" data-id="element-508">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-509">
                      <Check size={20} data-id="element-510" />
                    </div>
                    <span className="font-bold" data-id="element-511">Live CRM Walkthrough</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-512">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-513">
                      <Check size={20} data-id="element-514" />
                    </div>
                    <span className="font-bold" data-id="element-515">Automation Strategy</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-516">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-517">
                      <Check size={20} data-id="element-518" />
                    </div>
                    <span className="font-bold" data-id="element-519">Custom Pricing Quote</span>
                  </div>
                </div>
              </div>
              <div className="pt-12 border-t border-white/10" data-id="element-520">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-521">
                  Trusted by 10,000+ Agents
                </p>
              </div>
            </div>
            <div className="md:w-1/2 p-12" data-id="element-522">
              <h3 className="text-2xl font-black mb-8" data-id="element-523">Schedule Your Demo</h3>
              <form className="space-y-6" data-id="element-524">
                <div className="grid grid-cols-2 gap-4" data-id="element-525">
                  <div className="space-y-2" data-id="element-526">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-527">
                      First Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="John" data-id="element-528" />
                  </div>
                  <div className="space-y-2" data-id="element-529">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-530">
                      Last Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="Doe" data-id="element-531" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-532">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-533">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="john@agency.com" data-id="element-534" />
                </div>
                <div className="space-y-2" data-id="element-535">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-536">
                    Select Date
                  </label>
                  <div className="grid grid-cols-3 gap-2" data-id="element-537">
                    {['May 15', 'May 16', 'May 17'].map(d => <button key={d} type="button" className="py-2 bg-[#F3F6F9] border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-538">
                        {d}
                      </button>)}
                  </div>
                </div>
                <button type="submit" className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-539">
                  Confirm Meeting
                </button>
              </form>
            </div>
          </div>
        </motion.div>}

      {view === 'domains' ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="py-20 px-8" data-id="element-540">
          <div className="max-w-7xl mx-auto" data-id="element-541">
            <div className="text-center mb-16" data-id="element-542">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-543">
                Find Your Digital Home
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-544">
                Secure your domain and choose a hosting plan built for
                enterprise performance.
              </p>
            </div>

            {/* Domain Search */}
            <div className="max-w-3xl mx-auto mb-24" data-id="element-545">
              <div className="bg-white p-2 rounded-2xl shadow-2xl border border-[#D8E2EF] flex items-center gap-2" data-id="element-546">
                <div className="pl-6 text-[#1B264F]/30" data-id="element-547">
                  <LayoutIcon size={24} data-id="element-548" />
                </div>
                <input type="text" placeholder="search-your-dream-domain.com" className="flex-1 bg-transparent px-4 py-5 text-lg font-bold text-[#1B264F] focus:outline-none" data-id="element-549" />
                <button className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-550">
                  Search
                </button>
              </div>
              <div className="mt-6 flex justify-center gap-8 text-sm font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-551">
                <span data-id="element-552">.com $12.99</span>
                <span data-id="element-553">.net $14.99</span>
                <span data-id="element-554">.org $15.99</span>
                <span data-id="element-555">.ai $59.99</span>
              </div>
            </div>

            {/* Hosting Packages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-556">
              <div className="p-10 bg-white border border-[#D8E2EF] rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-557">
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-558">
                  Starter
                </h3>
                <div className="mb-8" data-id="element-559">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-560">
                    $29
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-561">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-562">
                  {['5 Pager Website', 'CRM Integration', 'Standard SSL', '24/7 Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-563">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-564" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#F3F6F9] text-[#1B264F] rounded-xl font-black hover:bg-[#D8E2EF] transition-all" data-id="element-565">
                  Select Starter
                </button>
              </div>

              <div className="p-10 bg-white border-2 border-[#00A1E0] rounded-3xl shadow-2xl relative scale-105" data-id="element-566">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00A1E0] text-white text-[10px] font-black uppercase tracking-widest rounded-full" data-id="element-567">
                  Recommended
                </div>
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-568">Pro</h3>
                <div className="mb-8" data-id="element-569">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-570">
                    $79
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-571">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-572">
                  {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-573">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-574" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-575">
                  Select Pro
                </button>
              </div>

              <div className="p-10 bg-[#1B264F] text-white rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-576">
                <h3 className="text-2xl font-black mb-2" data-id="element-577">Advance</h3>
                <div className="mb-8" data-id="element-578">
                  <span className="text-4xl font-black" data-id="element-579">$199</span>
                  <span className="text-white/40 font-bold ml-2" data-id="element-580">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-581">
                  {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-white/80" data-id="element-582">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-583" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-black transition-all" data-id="element-584">
                  Select Advance
                </button>
              </div>
            </div>
          </div>
        </motion.div> : <>
          {/* Hero Section */}
          <section className="relative pt-10 pb-20 px-8 overflow-hidden min-h-[80vh] flex items-center" data-id="element-585">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0" data-id="element-586">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000" alt="Real Estate Agent" className="w-full h-full object-cover" data-id="element-587" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/90 to-[#1B264F]/40" data-id="element-588"></div>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-16 items-center relative z-10" data-id="element-589">
              <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-590">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-591">
                  <span className="relative flex h-2 w-2" data-id="element-592">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00A1E0] opacity-75" data-id="element-593"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00A1E0]" data-id="element-594"></span>
                  </span>
                  Next-Gen CRM for High-Performers
                </div>
                <h1 className="text-4xl lg:text-7xl font-black text-white leading-[0.9] mb-8 tracking-tighter" data-id="element-595">
                  Organize.
                  <br data-id="element-596" />
                  Scale.
                  <br data-id="element-597" />
                  <span className="text-[#00A1E0]" data-id="element-598">Dominate.</span>
                </h1>
                <p className="text-xl text-white/70 max-w-xl mb-10 leading-relaxed font-medium" data-id="element-599">
                  The enterprise-grade command center for agents who refuse to
                  settle. Automate your workflow, track every lead, and crush
                  your quotas with Agenttoboss.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-600">
                  <button onClick={() => setView('demo')} className="w-full sm:w-auto px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-bold text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30 flex items-center justify-center gap-2 group" data-id="element-601">
                    Demo{' '}
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-602" />
                  </button>
                  <button className="w-full sm:w-auto px-10 py-5 bg-white text-[#1B264F] border border-[#D8E2EF] rounded-xl font-bold text-lg hover:bg-[#F3F6F9] transition-all" data-id="element-603">
                    Watch Video
                  </button>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            rotateY: -10
          }} animate={{
            opacity: 1,
            scale: 1,
            rotateY: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative perspective-1000" data-id="element-604">
                <div className="bg-white rounded-2xl shadow-[0_32px_64px_-12px_rgba(27,38,79,0.2)] border border-[#D8E2EF] p-6 overflow-hidden" data-id="element-605">
                  <div className="flex items-center justify-between mb-8" data-id="element-606">
                    <div className="flex gap-1.5" data-id="element-607">
                      <div className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-608"></div>
                      <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-609"></div>
                      <div className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-610"></div>
                    </div>
                    <div className="px-3 py-1 bg-[#F3F6F9] rounded-md text-[10px] font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-611">
                      Lead Funnel v2.4
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-612">
                    <div data-id="element-613">
                      <div className="flex justify-between mb-2" data-id="element-614">
                        <span className="text-xs font-bold text-[#1B264F]" data-id="element-615">
                          Active Lead Funnel
                        </span>
                        <span className="text-xs font-bold text-[#00A1E0]" data-id="element-616">
                          84% Efficiency
                        </span>
                      </div>
                      <div className="h-24 flex items-end gap-1" data-id="element-617">
                        {[40, 70, 45, 90, 65, 80, 55, 95, 75, 85].map((h, i) => <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height: `${h}%`
                    }} transition={{
                      delay: 0.5 + i * 0.05
                    }} className={`flex-1 rounded-t-sm ${i === 7 ? 'bg-[#00A1E0]' : 'bg-[#00A1E0]/20'}`} data-id="element-618" />)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-619">
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-620">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-621">
                          New Leads
                        </p>
                        <p className="text-2xl font-black text-[#1B264F]" data-id="element-622">
                          1,284
                        </p>
                      </div>
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-623">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-624">
                          Conversion
                        </p>
                        <p className="text-2xl font-black text-[#00A1E0]" data-id="element-625">
                          +12.4%
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-626">
                      <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase" data-id="element-627">
                        Active Reminders
                      </p>
                      {[{
                    title: 'Follow up with Sarah J.',
                    time: 'In 12 mins',
                    icon: Users
                  }, {
                    title: 'Quarterly Review - Acme Corp',
                    time: 'In 2 hours',
                    icon: BarChart3
                  }].map((rem, i) => <div key={i} className="flex items-center justify-between p-3 bg-white border border-[#D8E2EF] rounded-lg shadow-sm" data-id="element-628">
                          <div className="flex items-center gap-3" data-id="element-629">
                            <div className="w-8 h-8 bg-[#00A1E0]/10 rounded flex items-center justify-center text-[#00A1E0]" data-id="element-630">
                              <rem.icon size={16} data-id="element-631" />
                            </div>
                            <span className="text-xs font-bold" data-id="element-632">
                              {rem.title}
                            </span>
                          </div>
                          <span className="text-[10px] font-bold text-[#00A1E0]" data-id="element-633">
                            {rem.time}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Decorative Glow */}
                <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#00A1E0]/5 blur-[100px] rounded-full" data-id="element-634"></div>
              </motion.div>
            </div>
          </section>

          {/* Process Grid */}
          <section className="py-24 px-8 bg-white border-y border-[#D8E2EF]" data-id="element-635">
            <div className="max-w-7xl mx-auto" data-id="element-636">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-637">
                {[{
              title: 'Automated Sourcing',
              desc: 'AI-driven lead discovery across 50+ platforms.',
              icon: Target,
              image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Smart Enrichment',
              desc: 'Instant data validation and profile building.',
              icon: Layers,
              image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Agentic Outreach',
              desc: 'Personalized sequences that sound human.',
              icon: MousePointer2,
              image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Real-time Analytics',
              desc: 'Deep insights into every stage of your funnel.',
              icon: BarChart3,
              image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800'
            }].map((card, i) => <motion.div key={i} whileHover={{
              y: -8
            }} className="bg-white border border-[#1B264F]/10 rounded-[12px] hover:border-[#00A1E0] hover:shadow-xl hover:shadow-[#00A1E0]/5 transition-all group overflow-hidden" data-id="element-638">
                    <div className="aspect-video w-full overflow-hidden" data-id="element-639">
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-640" />
                    </div>
                    <div className="p-8" data-id="element-641">
                      <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-lg flex items-center justify-center text-[#00A1E0] mb-6 group-hover:bg-[#00A1E0] group-hover:text-white transition-colors" data-id="element-642">
                        <card.icon size={24} data-id="element-643" />
                      </div>
                      <h3 className="text-lg font-black text-[#1B264F] mb-3" data-id="element-644">
                        {card.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 leading-relaxed font-medium" data-id="element-645">
                        {card.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </section>

          {/* CRM Feature Bento - Progressive Disclosure */}
          <section className="py-32 px-8" data-id="element-646">
            <div className="max-w-7xl mx-auto" data-id="element-647">
              <div className="text-center mb-20" data-id="element-648">
                <h2 className="text-4xl md:text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-649">
                  The CRM Feature Bento
                </h2>
                <p className="text-lg text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-650">
                  Everything you need to manage your empire, built with the
                  precision of the Salesforce Lightning system.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[350px,1fr] gap-12 items-start" data-id="element-651">
                <div className="space-y-4" data-id="element-652">
                  {features.map(f => <button key={f.id} onClick={() => setActiveFeature(f.id)} className={`w-full text-left p-6 rounded-2xl border transition-all ${activeFeature === f.id ? 'bg-white border-[#00A1E0] shadow-xl shadow-[#00A1E0]/5 ring-1 ring-[#00A1E0]' : 'bg-transparent border-transparent hover:bg-white/50 hover:border-[#D8E2EF]'}`} data-id="element-653">
                      <h3 className={`text-lg font-black mb-2 ${activeFeature === f.id ? 'text-[#00A1E0]' : 'text-[#1B264F]'}`} data-id="element-654">
                        {f.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-655">
                        {f.desc.split('.')[0]}.
                      </p>
                    </button>)}
                </div>

                <div className="relative bg-white rounded-3xl border border-[#D8E2EF] shadow-2xl overflow-hidden aspect-[16/10]" data-id="element-656">
                  <AnimatePresence mode="wait" data-id="element-657">
                    <motion.div key={activeFeature} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.4
                }} className="absolute inset-0 p-8" data-id="element-658">
                      <div className="h-full w-full rounded-xl overflow-hidden relative group" data-id="element-659">
                        <img src={features.find(f => f.id === activeFeature)?.image} alt={activeFeature} className="w-full h-full object-cover" data-id="element-660" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1B264F]/80 to-transparent flex items-end p-12" data-id="element-661">
                          <div className="max-w-xl" data-id="element-662">
                            <h4 className="text-3xl font-black text-white mb-4" data-id="element-663">
                              {features.find(f => f.id === activeFeature)?.title}
                            </h4>
                            <p className="text-white/80 font-medium leading-relaxed" data-id="element-664">
                              {features.find(f => f.id === activeFeature)?.desc}
                            </p>
                          </div>
                        </div>

                        {/* Agentic Glow Effect */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none" data-id="element-665">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000" data-id="element-666"></div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section className="py-32 px-8 bg-[#1B264F] text-white overflow-hidden relative" data-id="element-667">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10" data-id="element-668">
              <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-[#00A1E0] blur-[150px] rounded-full" data-id="element-669"></div>
              <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00A1E0] blur-[120px] rounded-full" data-id="element-670"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10" data-id="element-671">
              <div className="text-center mb-20" data-id="element-672">
                <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight" data-id="element-673">
                  Simple, Enterprise Pricing
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto font-medium" data-id="element-674">
                  Choose the plan that fits your scale. No hidden fees, just
                  pure performance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-id="element-675">
                {/* Starter Plan */}
                <div className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-676">
                  <h3 className="text-xl font-black mb-2" data-id="element-677">Starter</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-678">
                    For solo agents getting started.
                  </p>
                  <div className="mb-10" data-id="element-679">
                    <span className="text-5xl font-black" data-id="element-680">$99</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-681">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-682">
                    {['5 Pager Website', 'CRM Integration', 'Standard SSL', 'Basic Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-683">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-684" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-685">
                    Demo
                  </button>
                </div>

                {/* Pro Plan - Highlighted */}
                <div className="p-1 bg-[#00A1E0] rounded-[32px] shadow-2xl shadow-[#00A1E0]/20 transform lg:scale-110 relative" data-id="element-686">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1.5 bg-white text-[#00A1E0] text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-xl" data-id="element-687">
                    Most Popular
                  </div>
                  <div className="p-10 bg-[#1B264F] rounded-[28px]" data-id="element-688">
                    <h3 className="text-2xl font-black mb-2 text-[#00A1E0]" data-id="element-689">
                      Pro
                    </h3>
                    <p className="text-white/60 text-sm font-medium mb-8" data-id="element-690">
                      For high-performers and small teams.
                    </p>
                    <div className="mb-10" data-id="element-691">
                      <span className="text-6xl font-black" data-id="element-692">$249</span>
                      <span className="text-white/40 font-bold ml-2" data-id="element-693">/mo</span>
                    </div>
                    <ul className="space-y-4 mb-10" data-id="element-694">
                      {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority 24/7 Support', 'Advanced Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold" data-id="element-695">
                          <Check size={18} className="text-[#00A1E0]" data-id="element-696" /> {f}
                        </li>)}
                    </ul>
                    <button onClick={() => setView('demo')} className="w-full py-5 bg-[#00A1E0] hover:bg-[#0089C4] text-white rounded-xl font-black text-lg transition-all shadow-xl shadow-[#00A1E0]/20" data-id="element-697">
                      Go Pro Mode
                    </button>
                  </div>
                </div>

                {/* Advance Plan */}
                <div className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-698">
                  <h3 className="text-xl font-black mb-2" data-id="element-699">Advance</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-700">
                    For large agencies and brokerage.
                  </p>
                  <div className="mb-10" data-id="element-701">
                    <span className="text-5xl font-black" data-id="element-702">$899</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-703">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-704">
                    {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-705">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-706" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-707">
                    Demo
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Contact / Callback Section */}
          <section className="py-32 px-8 bg-white" data-id="element-708">
            <div className="max-w-7xl mx-auto" data-id="element-709">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-710">
                <div data-id="element-711">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-712">
                    Request a <span className="text-[#00A1E0]" data-id="element-713">Callback</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-8" data-id="element-714">
                    Have specific questions about our platform or need help
                    choosing the right plan? Our team of experts is ready to
                    help you scale.
                  </p>
                  <div className="space-y-6" data-id="element-715">
                    <div className="flex items-center gap-4" data-id="element-716">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-717">
                        <Zap size={24} data-id="element-718" />
                      </div>
                      <div data-id="element-719">
                        <p className="font-black text-[#1B264F]" data-id="element-720">
                          Instant Response
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-721">
                          Average callback time: 15 minutes
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-722">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-723">
                        <Shield size={24} data-id="element-724" />
                      </div>
                      <div data-id="element-725">
                        <p className="font-black text-[#1B264F]" data-id="element-726">
                          Expert Guidance
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-727">
                          Speak directly with a scaling specialist
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F3F6F9] p-10 rounded-[40px] border border-[#D8E2EF] shadow-inner" data-id="element-728">
                  <form className="space-y-6" data-id="element-729">
                    <div className="space-y-2" data-id="element-730">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-731">
                        Full Name
                      </label>
                      <input type="text" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Jane Smith" data-id="element-732" />
                    </div>
                    <div className="space-y-2" data-id="element-733">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-734">
                        Phone Number
                      </label>
                      <input type="tel" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="+1 (555) 000-0000" data-id="element-735" />
                    </div>
                    <div className="space-y-2" data-id="element-736">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-737">
                        Best Time to Call
                      </label>
                      <select className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-738">
                        <option data-id="element-739">As soon as possible</option>
                        <option data-id="element-740">Morning (9AM - 12PM)</option>
                        <option data-id="element-741">Afternoon (12PM - 5PM)</option>
                        <option data-id="element-742">Evening (5PM - 8PM)</option>
                      </select>
                    </div>
                    <button className="w-full py-5 bg-[#1B264F] text-white rounded-xl font-black text-lg hover:bg-[#2a3a75] transition-all shadow-xl shadow-[#1B264F]/10" data-id="element-743">
                      Request Callback
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* Sign-Up Form */}
          <section className="py-32 px-8 bg-white" data-id="element-744">
            <div className="max-w-4xl mx-auto text-center" data-id="element-745">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#00A1E0]/10 rounded-3xl text-[#00A1E0] mb-8" data-id="element-746">
                <Shield size={40} data-id="element-747" />
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-[#1B264F] mb-8 tracking-tighter" data-id="element-748">
                Ready to become a Boss?
              </h2>
              <p className="text-xl text-[#1B264F]/60 mb-12 font-medium" data-id="element-749">
                Join 10,000+ top-performing agents who have scaled their
                business with Agenttoboss.
              </p>

              <div className="bg-[#F3F6F9] p-2 rounded-2xl border border-[#D8E2EF] flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto shadow-inner" data-id="element-750">
                <input type="email" placeholder="Enter your work email" className="flex-1 bg-transparent px-6 py-4 text-[#1B264F] font-bold focus:outline-none placeholder:text-[#1B264F]/30" data-id="element-751" />
                <button className="px-8 py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20 whitespace-nowrap" data-id="element-752">
                  One-Click Sign Up
                </button>
              </div>
              <p className="mt-6 text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-753">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </section>
        </>}

      {/* Shared Footer for all storefront views */}
      <footer className="bg-[#F3F6F9] border-t border-[#D8E2EF] py-20 px-8 mt-20" data-id="element-754">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12" data-id="element-755">
          <div className="col-span-1 md:col-span-1" data-id="element-756">
            <div className="flex items-center gap-2 mb-6" data-id="element-757">
              <div className="w-8 h-8 bg-[#00A1E0] rounded flex items-center justify-center text-white" data-id="element-758">
                <Zap size={18} fill="currentColor" data-id="element-759" />
              </div>
              <span className="text-lg font-black tracking-tight text-[#1B264F]" data-id="element-760">
                Agent<span className="text-[#00A1E0]" data-id="element-761">to</span>boss
              </span>
            </div>
            <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-762">
              The definitive command center for the modern enterprise agent.
              Built on the principles of scale and dominance.
            </p>
          </div>

          <div data-id="element-763">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-764">
              Platform
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-765">
              <li data-id="element-766">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-767">
                  CRM Core
                </a>
              </li>
              <li data-id="element-768">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-769">
                  Automations
                </a>
              </li>
              <li data-id="element-770">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-771">
                  Analytics
                </a>
              </li>
              <li data-id="element-772">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-773">
                  Integrations
                </a>
              </li>
            </ul>
          </div>

          <div data-id="element-774">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-775">
              Company
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-776">
              <li data-id="element-777">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-778">
                  About Us
                </a>
              </li>
              <li data-id="element-779">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-780">
                  Careers
                </a>
              </li>
              <li data-id="element-781">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-782">
                  Press Kit
                </a>
              </li>
              <li data-id="element-783">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-784">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          <div data-id="element-785">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-786">
              Legal
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-787">
              <li data-id="element-788">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-789">
                  Privacy Policy
                </a>
              </li>
              <li data-id="element-790">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-791">
                  Terms of Service
                </a>
              </li>
              <li data-id="element-792">
                <a href="#" className="hover:text-[#00A1E0] transition-colors" data-id="element-793">
                  Security
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-[#D8E2EF] flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-794">
          <p className="text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-795">
            © 2026 Agenttoboss Inc. All rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-796">
            <div className="w-5 h-5 bg-[#1B264F]/10 rounded-full" data-id="element-797"></div>
            <div className="w-5 h-5 bg-[#1B264F]/10 rounded-full" data-id="element-798"></div>
            <div className="w-5 h-5 bg-[#1B264F]/10 rounded-full" data-id="element-799"></div>
          </div>
        </div>
      </footer>
    </div>;
}